import {useEffect, useState} from "react";
import {Link} from "react-router-dom";
import {Bug, Palette, ShoppingBag, Store, Workflow} from "lucide-react";
import {
  getAdminSiteSettings,
  setApiDebugEnabled,
  updateAdminSiteSettings,
  type AdminSiteSettings,
  type ShopAccessMode
} from "@corporatesclub/shared";
import {applySiteAccentColor} from "@/lib/applySiteAccent";
import {adminToast} from "@/lib/adminToast";
import {cn} from "@/lib/utils";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {Label} from "@/components/ui/label";
import {Switch} from "@/components/ui/switch";
import {Textarea} from "@/components/ui/textarea";
import {RadioGroup, RadioGroupItem} from "@/components/ui/radio-group";
import {AdminAlert, AdminCard, AdminPageLayout} from "./ui";

const DEFAULT: AdminSiteSettings = {
  accentColor: "#ff4200",
  shopEnabled: true,
  ordersEnabled: true,
  closedMessage: "The store is temporarily closed. Please check back soon.",
  accessMode: "login_required",
  publicAccess: false,
  guestPricesVisible: false,
  guestOrdersAllowed: false,
  symphonyaAutoSend: true,
  debugErrors: false
};

const ACCESS_OPTIONS: {value: ShopAccessMode; title: string; description: string}[] = [
  {
    value: "login_required",
    title: "Login required",
    description: "The entire store is protected. Visitors must sign in to browse, see prices, or order."
  },
  {
    value: "public_full",
    title: "Public — full access",
    description: "Browse, prices, cart, and checkout without an account (guest checkout enabled)."
  },
  {
    value: "public_no_prices",
    title: "Public — hide prices",
    description: "Browse the catalog without prices. Sign in to see prices and place orders."
  },
  {
    value: "public_login_to_order",
    title: "Public — prices visible, login to order",
    description: "Visitors see prices but must sign in to add to cart or checkout."
  }
];

type SettingsCategory = "appearance" | "store" | "checkout" | "integrations";

const TABS: {id: SettingsCategory; label: string; description: string; icon: typeof Palette}[] = [
  {id: "appearance", label: "Appearance", description: "Applied in the store (Bixoto) and admin.", icon: Palette},
  {id: "store", label: "Store", description: "Who can browse, see prices, and place orders without signing in.", icon: Store},
  {id: "checkout", label: "Checkout", description: "Control whether customers can place orders.", icon: ShoppingBag},
  {id: "integrations", label: "Integrations", description: "Symphonya auto-push and debug error visibility.", icon: Workflow}
];

export function SettingsView() {
  const [form, setForm] = useState<AdminSiteSettings>(DEFAULT);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [category, setCategory] = useState<SettingsCategory>("appearance");
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    getAdminSiteSettings()
      .then((s) => {
        setForm({...DEFAULT, ...s});
        setApiDebugEnabled(!!s.debugErrors);
        setLoadFailed(false);
      })
      .catch((e: Error) => { setErr(e.message); setLoadFailed(true); });
  }, []);

  async function save() {
    setBusy(true);
    setErr("");
    try {
      const saved = await updateAdminSiteSettings(form);
      setForm({...DEFAULT, ...saved});
      setApiDebugEnabled(!!saved.debugErrors);
      applySiteAccentColor(saved.accentColor, document.querySelector(".admin-theme") as HTMLElement || document);
      adminToast.success("Settings saved.");
    } catch(e: unknown) {
      setErr(e instanceof Error ? e.message : "Error saving");
    } finally {
      setBusy(false);
    }
  }

  const activeTab = TABS.find((t) => t.id === category) ?? TABS[0];

  if(loadFailed && err) {
    return (
      <AdminPageLayout title="Site settings" breadcrumbs={[{label: "Dashboard", href: "/vadmin"}, {label: "Settings"}]}>
        <AdminAlert variant="error">{err}</AdminAlert>
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout
      title="Site settings"
      description="Global settings for the store and admin panel."
      breadcrumbs={[{label: "Dashboard", href: "/vadmin"}, {label: "Settings"}]}
      actions={
        <Button type="button" onClick={() => save()} disabled={busy}>
          {busy ? "Saving..." : "Save settings"}
        </Button>
      }
    >
      {err ? <AdminAlert variant="error">{err}</AdminAlert> : null}

      <div className="admin-page-tabs" role="tablist" aria-label="Settings sections">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={category === tab.id}
              className={cn("admin-page-tabs__btn", category === tab.id && "isActive")}
              onClick={() => setCategory(tab.id)}
            >
              <Icon aria-hidden />
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="grid max-w-3xl gap-6">
        {category === "appearance" ? (
          <AdminCard title={activeTab.label} description={activeTab.description}>
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-2">
                <Label htmlFor="accent-color">Color code</Label>
                <Input
                  id="accent-color"
                  value={form.accentColor}
                  onChange={(e) => setForm({...form, accentColor: e.target.value})}
                  placeholder="#ff4200"
                  className="w-40 font-mono"
                />
              </div>
              <input
                type="color"
                value={/^#[0-9a-fA-F]{6}$/.test(form.accentColor) ? form.accentColor : "#ff4200"}
                onChange={(e) => setForm({...form, accentColor: e.target.value})}
                className="h-10 w-14 cursor-pointer rounded-md border border-border"
                aria-label="Select color"
              />
              <div
                className="flex h-10 min-w-[120px] items-center justify-center rounded-lg px-4 text-sm font-medium text-white"
                style={{backgroundColor: form.accentColor}}
              >
                Preview accent
              </div>
            </div>
          </AdminCard>
        ) : null}

        {category === "store" ? (
          <AdminCard title={activeTab.label} description={activeTab.description}>
            <div className="mb-5 flex items-center justify-between gap-4 rounded-xl border border-border/70 p-4">
              <div>
                <p className="font-medium">Store active</p>
                <p className="text-sm text-muted-foreground">Disabled = temporary store closed message.</p>
              </div>
              <Switch checked={form.shopEnabled} onCheckedChange={(v) => setForm({...form, shopEnabled: v})} />
            </div>
            <RadioGroup
              value={form.accessMode}
              onValueChange={(v) => setForm({...form, accessMode: v as ShopAccessMode})}
              className="gap-3"
            >
              {ACCESS_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  htmlFor={`access-${opt.value}`}
                  className="flex cursor-pointer items-start gap-3 rounded-xl border border-border/70 p-4 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring"
                >
                  <RadioGroupItem id={`access-${opt.value}`} value={opt.value} className="mt-0.5" />
                  <span>
                    <span className="block font-medium">{opt.title}</span>
                    <span className="mt-1 block text-sm text-muted-foreground">{opt.description}</span>
                  </span>
                </label>
              ))}
            </RadioGroup>
          </AdminCard>
        ) : null}

        {category === "checkout" ? (
          <AdminCard title={activeTab.label} description={activeTab.description}>
            <div className="mb-5 flex items-center justify-between gap-4 rounded-xl border border-border/70 p-4">
              <div>
                <p className="font-medium">Orders allowed</p>
                <p className="text-sm text-muted-foreground">Blocks checkout and order placement globally.</p>
              </div>
              <Switch checked={form.ordersEnabled} onCheckedChange={(v) => setForm({...form, ordersEnabled: v})} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="closed-msg">Store closed message</Label>
              <Textarea
                id="closed-msg"
                rows={3}
                value={form.closedMessage}
                onChange={(e) => setForm({...form, closedMessage: e.target.value})}
                placeholder="The store is temporarily closed..."
              />
            </div>
          </AdminCard>
        ) : null}

        {category === "integrations" ? (
          <>
            <AdminCard title="Symphonya" description="Control automatic ERP push after checkout.">
              <div className="flex items-center justify-between gap-4 rounded-xl border border-border/70 p-4">
                <div>
                  <p className="font-medium">Send orders to Symphonya automatically</p>
                  <p className="text-sm text-muted-foreground">
                    When on, Bucharest warehouse orders are pushed to Symphonya right after placement.
                    When off, orders stay pending (`_symphonya=0`) until you send them from{" "}
                    <Link to="/vadmin/integrari" className="underline">Integrations</Link> or cron.
                  </p>
                </div>
                <Switch
                  checked={form.symphonyaAutoSend}
                  onCheckedChange={(v) => setForm({...form, symphonyaAutoSend: v})}
                />
              </div>
            </AdminCard>
            <AdminCard title="Debug" description="Show technical error details while troubleshooting.">
              <div className="flex items-center justify-between gap-4 rounded-xl border border-border/70 p-4">
                <div className="flex items-start gap-3">
                  <Bug className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                  <div>
                    <p className="font-medium">Show API errors in console</p>
                    <p className="text-sm text-muted-foreground">
                      When on, failed API calls log full responses in the browser console (shop + admin),
                      and INTERNAL 500 responses include `detail` / `file`. Turn off on production when done debugging.
                    </p>
                  </div>
                </div>
                <Switch
                  checked={form.debugErrors}
                  onCheckedChange={(v) => {
                    setForm({...form, debugErrors: v});
                    setApiDebugEnabled(v);
                  }}
                />
              </div>
            </AdminCard>
          </>
        ) : null}
      </div>
    </AdminPageLayout>
  );
}
